import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, LayoutGrid, List, ListChecks, Pencil } from "lucide-react";
import { fmtDate, fmtRelative, todayIso } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import EmptyState from "@/components/EmptyState";

const STATUSES = ["Backlog", "To Do", "In Progress", "Blocked", "Internal Review", "Client Approval", "Revision", "Completed", "Cancelled"];
const KANBAN_STATUSES = ["Backlog", "To Do", "In Progress", "Internal Review", "Client Approval", "Completed"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

const empty = {
  name: "", description: "", client_id: "", project_id: "", assignee_id: "", collaborator_ids: [],
  start_date: "", due_date: todayIso(), estimated_hours: 0, priority: "Medium", status: "To Do", progress: 0, tags: [],
};

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("list");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [priorityF, setPriorityF] = useState("");
  const [assigneeF, setAssigneeF] = useState(params.get("assignee_id") || "");
  const [mine, setMine] = useState(params.get("mine") === "true");
  const [open, setOpen] = useState(params.get("new") === "1");
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [comments, setComments] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [newComment, setNewComment] = useState("");

  const { user } = useAuth();
  const canCreate = user?.role === "super_admin" || user?.role === "admin";
  const dueFilter = params.get("due") || ""; // today | week | overdue

  const load = async () => {
    const p = {};
    if (q) p.q = q;
    if (statusF) p.status = statusF;
    if (priorityF) p.priority = priorityF;
    if (assigneeF) p.assignee_id = assigneeF;
    if (mine) p.mine = true;
    const r = await api.get("/tasks", { params: p });
    let data = r.data;
    if (dueFilter) {
      const today = todayIso();
      const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
      data = data.filter((t) => {
        if (["Completed", "Cancelled"].includes(t.status)) return false;
        if (!t.due_date) return false;
        if (dueFilter === "today") return t.due_date === today;
        if (dueFilter === "week") return t.due_date >= today && t.due_date <= weekEnd;
        if (dueFilter === "overdue") return t.due_date < today;
        return true;
      });
    }
    setTasks(data);
  };
  useEffect(() => { load(); }, [q, statusF, priorityF, assigneeF, mine, dueFilter]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  const openTask = async (t) => {
    setDetail(t);
    const [c, tr] = await Promise.all([
      api.get(`/tasks/${t.id}/comments`),
      api.get("/activity", { params: { entity_type: "task", entity_id: t.id, action: "task.transfer", limit: 50 } }),
    ]);
    setComments(c.data);
    setTransfers(tr.data);
  };

  const save = async () => {
    if (!form.name) { toast.error("Task name required"); return; }
    try {
      if (editingId) {
        await api.put(`/tasks/${editingId}`, form);
        toast.success("Task updated");
        setDetail(null);
      } else {
        await api.post("/tasks", form);
        toast.success("Task created");
      }
      setOpen(false); setForm(empty); setEditingId(null); setParams({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name || "", description: t.description || "",
      client_id: t.client_id || "", project_id: t.project_id || "", service_id: t.service_id || "",
      assignee_id: t.assignee_id || "", collaborator_ids: t.collaborator_ids || [],
      start_date: t.start_date || "", due_date: t.due_date || todayIso(),
      estimated_hours: t.estimated_hours || 0,
      priority: t.priority || "Medium", status: t.status || "To Do",
      progress: t.progress || 0, tags: t.tags || [], checklist: t.checklist || [],
    });
    setDetail(null);
    setOpen(true);
  };

  const changeStatus = async (task, newStatus) => {
    try {
      await api.patch(`/tasks/${task.id}/status`, { status: newStatus });
      load();
      if (detail?.id === task.id) setDetail({ ...detail, status: newStatus });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addComment = async () => {
    if (!newComment.trim() || !detail) return;
    const r = await api.post(`/tasks/${detail.id}/comments`, { text: newComment });
    setComments([...comments, r.data]);
    setNewComment("");
  };

  const transferTask = async (newAssigneeId) => {
    if (!detail || !newAssigneeId || newAssigneeId === detail.assignee_id) return;
    try {
      const r = await api.patch(`/tasks/${detail.id}/assignee`, { assignee_id: newAssigneeId });
      const newAssignee = users.find((u) => u.id === newAssigneeId);
      toast.success(`Transferred to ${newAssignee?.name || "team member"}`);
      setDetail(r.data);
      const tr = await api.get("/activity", { params: { entity_type: "task", entity_id: detail.id, action: "task.transfer", limit: 50 } });
      setTransfers(tr.data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = useMemo(() => {
    const g = {}; KANBAN_STATUSES.forEach((s) => (g[s] = []));
    tasks.forEach((t) => { if (g[t.status]) g[t.status].push(t); });
    return g;
  }, [tasks]);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Execution</div>
          <h1 className="text-4xl font-black tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
            {tasks.length} task{tasks.length !== 1 && "s"}
            {dueFilter && (
              <span data-testid="tasks-due-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest bg-primary/15 text-primary">
                {dueFilter === "today" && "Due today"}
                {dueFilter === "week" && "Due this week"}
                {dueFilter === "overdue" && "Overdue"}
                <button
                  data-testid="tasks-due-clear"
                  onClick={() => { const p = new URLSearchParams(params); p.delete("due"); setParams(p); }}
                  className="ml-1 hover:text-foreground"
                  aria-label="Clear filter"
                >×</button>
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="border border-border rounded-md flex overflow-hidden">
            <button data-testid="view-list-btn" onClick={() => setView("list")} className={`px-3 py-2 text-xs flex items-center gap-1 ${view === "list" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}><List size={14} /> List</button>
            <button data-testid="view-kanban-btn" onClick={() => setView("kanban")} className={`px-3 py-2 text-xs flex items-center gap-1 ${view === "kanban" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}><LayoutGrid size={14} /> Kanban</button>
          </div>
          {canCreate && (
            <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setParams({}); setEditingId(null); setForm(empty); } }}>
              <SheetTrigger asChild><Button data-testid="new-task-btn" className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />New Task</Button></SheetTrigger>
              <SheetContent className="sm:max-w-lg overflow-y-auto">
                <SheetHeader><SheetTitle>{editingId ? "Edit Task" : "New Task"}</SheetTitle></SheetHeader>
                <div className="mt-5 space-y-3">
                  <div><Label className="font-mono text-[10px] uppercase">Name *</Label><Input data-testid="task-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Client</Label>
                      <Select value={form.client_id || "__none__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="task-form-client"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="__none__">— None —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="font-mono text-[10px] uppercase">Project</Label>
                      <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="__none__">— None —</SelectItem>{projects.filter((p) => !form.client_id || p.client_id === form.client_id).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Assignee</Label>
                      <Select value={form.assignee_id || "__none__"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "__none__" ? "" : v })}>
                        <SelectTrigger data-testid="task-form-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent><SelectItem value="__none__">— Unassigned —</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="font-mono text-[10px] uppercase">Due date</Label><Input data-testid="task-form-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Priority</Label>
                      <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="font-mono text-[10px] uppercase">Status</Label>
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <Button data-testid="task-form-submit" onClick={save} className="w-full bg-primary hover:bg-primary/90">{editingId ? "Save changes" : "Create Task"}</Button>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="tasks-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 w-56" />
        </div>
        <Select value={statusF || "__all__"} onValueChange={(v) => setStatusF(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={priorityF || "__all__"} onValueChange={(v) => setPriorityF(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All priorities" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All priorities</SelectItem>{PRIORITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={assigneeF || "__all__"} onValueChange={(v) => setAssigneeF(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="tasks-assignee-filter" className="w-48"><SelectValue placeholder="All assignees" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All assignees</SelectItem>
            {users.filter((u) => u.status === "active").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dueFilter || "__all__"} onValueChange={(v) => { const p = new URLSearchParams(params); if (v === "__all__") p.delete("due"); else p.set("due", v); setParams(p); }}>
          <SelectTrigger data-testid="tasks-due-filter" className="w-40"><SelectValue placeholder="Any due date" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any due date</SelectItem>
            <SelectItem value="today">Due today</SelectItem>
            <SelectItem value="week">Due this week</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 text-sm">
          <input data-testid="tasks-mine-toggle" type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="rounded" />
          My tasks only
        </label>
      </div>

      {tasks.length === 0 ? <EmptyState icon={ListChecks} title="No tasks match" description="Try clearing filters or create a new task." /> :
        view === "list" ? (
          <div className="border border-border rounded-md overflow-hidden bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <th className="px-3 py-2.5">Task</th>
                    <th className="px-3 py-2.5">Assignee</th>
                    <th className="px-3 py-2.5">Due</th>
                    <th className="px-3 py-2.5">Priority</th>
                    <th className="px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t) => {
                    const client = clients.find((c) => c.id === t.client_id);
                    const assignee = users.find((u) => u.id === t.assignee_id);
                    return (
                      <tr key={t.id} className="data-row border-t border-border cursor-pointer" onClick={() => openTask(t)} data-testid={`task-row-${t.id}`}>
                        <td className="px-3 py-2.5">
                          <div className="font-semibold">{t.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{t.task_id} · {client?.company_name || "—"}</div>
                        </td>
                        <td className="px-3 py-2.5"><div className="flex items-center gap-2"><UserAvatar name={assignee?.name} size={22} /><span className="text-xs">{assignee?.name || "Unassigned"}</span></div></td>
                        <td className="px-3 py-2.5 font-mono text-xs">{fmtDate(t.due_date)}</td>
                        <td className="px-3 py-2.5"><StatusBadge value={t.priority} /></td>
                        <td className="px-3 py-2.5">
                          <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                            <SelectTrigger onClick={(e) => e.stopPropagation()} className="h-7 text-xs w-40 border-0 bg-transparent p-0 font-mono uppercase tracking-widest [&>svg]:hidden">
                              <StatusBadge value={t.status} />
                            </SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-3 kanban-col">
            {KANBAN_STATUSES.map((s) => (
              <div key={s} className="w-72 shrink-0 border border-border rounded-md bg-card" data-testid={`kanban-col-${s}`}>
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <div><StatusBadge value={s} /></div>
                  <span className="font-mono text-[10px] text-muted-foreground">{grouped[s]?.length || 0}</span>
                </div>
                <div className="p-2 space-y-2 min-h-40 max-h-[calc(100vh-260px)] overflow-y-auto kanban-col">
                  {(grouped[s] || []).map((t) => {
                    const assignee = users.find((u) => u.id === t.assignee_id);
                    const client = clients.find((c) => c.id === t.client_id);
                    return (
                      <div key={t.id} onClick={() => openTask(t)} className="border border-border rounded-md p-3 bg-background hover:border-primary/40 cursor-pointer transition-colors" data-testid={`kanban-card-${t.id}`}>
                        <div className="text-[11px] text-muted-foreground font-mono">{client?.company_name || t.task_id}</div>
                        <div className="text-sm font-semibold mt-1">{t.name}</div>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1"><StatusBadge value={t.priority} /></div>
                          <div className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
                            <UserAvatar name={assignee?.name} size={18} />
                            {fmtDate(t.due_date)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      }

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{detail?.task_id}</div>
                <DialogTitle>{detail?.name}</DialogTitle>
              </div>
              {canCreate && detail && (
                <Button data-testid="task-edit-btn" size="sm" variant="outline" onClick={() => openEdit(detail)} className="shrink-0">
                  <Pencil size={12} className="mr-1" />Edit
                </Button>
              )}
            </div>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-mono text-[10px] uppercase text-muted-foreground">Due</span><div>{fmtDate(detail.due_date)}</div></div>
                <div><span className="font-mono text-[10px] uppercase text-muted-foreground">Priority</span><div><StatusBadge value={detail.priority} /></div></div>
                <div>
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">Status</span>
                  <div>
                    <Select value={detail.status} onValueChange={(v) => changeStatus(detail, v)}>
                      <SelectTrigger data-testid="task-detail-status" className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><span className="font-mono text-[10px] uppercase text-muted-foreground">Assignee</span>
                  <div>
                    <Select value={detail.assignee_id || "__none__"} onValueChange={(v) => transferTask(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="task-detail-assignee" className="h-8">
                        <SelectValue placeholder="Unassigned">{users.find((u) => u.id === detail.assignee_id)?.name || "Unassigned"}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter((u) => u.status === "active").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              {detail.description && <div className="text-sm border-t border-border pt-3">{detail.description}</div>}
              {transfers.length > 0 && (
                <div className="border-t border-border pt-3" data-testid="task-transfer-history">
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Transfer history ({transfers.length})</div>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {transfers.map((t) => (
                      <div key={t.id} className="text-xs flex items-center gap-2 border-l-2 border-primary/40 pl-2" data-testid={`transfer-${t.id}`}>
                        <span className="font-mono text-muted-foreground shrink-0">{fmtRelative(t.created_at)}</span>
                        <span className="text-muted-foreground">{t.actor_name} transferred</span>
                        <span className="font-mono">{t.meta?.from_name || "Unassigned"}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-mono font-semibold">{t.meta?.to_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-border pt-3">
                <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Comments</div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {comments.map((c) => (
                    <div key={c.id} className="text-sm border-l-2 border-primary/50 pl-3">
                      <div className="font-semibold text-xs">{c.user_name}</div>
                      <div>{c.text}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add comment..." data-testid="task-comment-input" onKeyDown={(e) => e.key === "Enter" && addComment()} />
                  <Button data-testid="task-comment-submit" onClick={addComment} size="sm">Post</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
